import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Trophy, Calendar, MapPin, Newspaper, ArrowRight, GraduationCap } from "lucide-react";
import { Link } from "react-router-dom";

export default function CoachDashboard() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get(`/directory/coach`, { params: { state: user?.state, limit: 12 } }).then((r) => setCoaches(r.data.items || [])).catch(() => {});
    api.get(`/championships`, { params: { upcoming: true } }).then((r) => setEvents(r.data.slice(0, 4))).catch(() => {});
    api.get(`/news`).then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, [user]);

  return (
    <DashboardShell title="Academy / Coach Portal" eyebrow={`Welcome, ${user?.name?.split(" ")[0] || "Member"}`}>
      {/* Profile + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <div className="lg:col-span-2 bg-gradient-to-br from-nkf-blue to-nkf-navy text-white rounded-md p-6 relative overflow-hidden">
          <div className="absolute inset-0 diagonal-stripes" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Federation Member</div>
            <div className="font-display text-2xl sm:text-3xl mt-2">{user?.name}</div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <Row label="Member ID" value={user?.membership_no || "Pending"} />
              <Row label="State" value={user?.state || "—"} />
              <Row label="District" value={user?.district || "—"} />
              <Row label="Status" value="Active" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <KPI label="Coaches in state" value={coaches.length} icon={GraduationCap} />
          <KPI label="Upcoming Events" value={events.length} icon={Trophy} />
        </div>
      </div>

      {/* Coaches nearby */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-nkf-blue">Other Coaches in {user?.state || "your state"}</h2>
          <Link to="/coaches" className="text-sm font-semibold text-nkf-blue hover:text-nkf-red inline-flex items-center gap-1">
            Full directory <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {coaches.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3 hover:border-nkf-blue/40 transition">
              <div className="h-10 w-10 rounded-full bg-nkf-red/10 flex items-center justify-center text-nkf-red font-display">{(c.name || "?").slice(0, 1)}</div>
              <div className="min-w-0">
                <div className="font-semibold text-nkf-blue truncate">{c.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.district || "—"}</div>
              </div>
            </div>
          ))}
          {!coaches.length && <div className="sm:col-span-2 lg:col-span-3 p-6 border border-dashed rounded-md text-slate-500 text-sm text-center">No registered coaches in {user?.state || "your state"} yet.</div>}
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-nkf-blue">Upcoming Events</h2>
          <Link to="/championships" className="text-sm font-semibold text-nkf-blue hover:text-nkf-red inline-flex items-center gap-1">All events <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {events.map((c) => (
            <Link key={c.id} to={`/championships/${c.id}`} className="bg-white border border-slate-200 rounded-md p-4 hover:border-nkf-blue/40 transition">
              <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{c.level}</div>
              <div className="font-display text-base text-nkf-blue mt-1">{c.title}</div>
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.start_date} · {c.location}</div>
            </Link>
          ))}
          {!events.length && <div className="md:col-span-2 p-6 border border-dashed rounded-md text-slate-500 text-sm text-center">No upcoming events.</div>}
        </div>
      </section>

      {/* News */}
      {news.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl text-nkf-blue mb-4 flex items-center gap-2"><Newspaper className="h-5 w-5 text-nkf-red" /> Latest Federation News</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {news.map((n) => (
              <article key={n.id} className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">{n.category}</div>
                <div className="font-display text-base text-nkf-blue mt-1">{n.title}</div>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{n.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </DashboardShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-display tracking-wide truncate">{value}</div>
    </div>
  );
}
function KPI({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-2xl text-nkf-blue mt-1 tabular-nums">{value}</div>
      </div>
      <Icon className="h-6 w-6 text-nkf-gold" />
    </div>
  );
}
